import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, catchError, of, switchMap, throwError} from 'rxjs';
import {AuthService} from '../_services/auth.service';
import {environment} from '../../environments/environment';

@Injectable()
export class RefreshInterceptor implements HttpInterceptor {
    constructor(private auth: AuthService) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!request.url.startsWith(`${environment.apiUrl}/`) ||
            /\/(?:token(?:\/refresh)?|users\/login(?:\/sso)?|users\/mfa\/magic-link)\/$/.test(request.url)) {
            return next.handle(request);
        }
        const access = localStorage.getItem('access_token');
        if (access) request = this.withTokens(request, access);
        const authenticated = request.headers.get('Authorization')?.startsWith('Bearer ');
        return next.handle(request).pipe(catchError(error => {
            if (!authenticated || error.status !== 401) return throwError(() => error);
            const current = localStorage.getItem('access_token');
            if (current !== access && (!current || !this.auth.sameSession(access, current))) {
                return throwError(() => new Error('The authentication session changed.'));
            }
            if (error.error?.code === 'session_expired') {
                this.auth.logout();
                return throwError(() => error);
            }
            if (error.error?.code !== 'token_not_valid') return throwError(() => error);
            // A late failure may belong to the pair that has already rotated.
            const refreshed = current && current !== access
                ? of({access: current})
                : localStorage.getItem('isApp') === 'true'
                    ? this.auth.refreshAppToken(access!)
                    : this.auth.refreshTokens();
            return refreshed.pipe(switchMap(pair => next.handle(this.withTokens(request, pair.access)).pipe(
                catchError(retryError => {
                    if (retryError.status === 401) {
                        if (localStorage.getItem('access_token') !== pair.access) {
                            return throwError(() => new Error('The authentication session changed.'));
                        }
                        this.auth.logout();
                    }
                    return throwError(() => retryError);
                })
            )));
        }));
    }

    private withTokens(request: HttpRequest<any>, access: string): HttpRequest<any> {
        return request.clone({
            setHeaders: {Authorization: `Bearer ${access}`},
            ...(request.url === `${environment.apiUrl}/users/logout/` ? {
                body: {...request.body, refresh: localStorage.getItem('refresh_token')}
            } : {})
        });
    }
}
